import { useState, useEffect } from 'react';
import { fetchProviderBase, fetchProviderMedia } from '../api/matchmakingApi';
import type { ApiProviderProfile } from '../types/matchmaking.types';

type UseProviderProfileProps = {
  providerProfile?: ApiProviderProfile;
  isExpanded: boolean;
  setProfileImages: (images: string[]) => void;
};

/**
 * Hook to manage provider profile data fetching
 */
export const useProviderProfile = ({
  providerProfile,
  isExpanded,
  setProfileImages,
}: UseProviderProfileProps) => {
  const [profileLoading, setProfileLoading] = useState(false);
  const [fetchedProfile, setFetchedProfile] = useState<any>(null);
  const [isProfileImageLoading, setIsProfileImageLoading] = useState(false);
  const [isBaseApiLoading, setIsBaseApiLoading] = useState(false);

  // Reset fetched profile when provider_id changes
  useEffect(() => {
    setFetchedProfile(null);
    setProfileImages([]);
  }, [providerProfile?.provider_id, setProfileImages]);

  const handleViewProfileClick = async () => {
    if (!isExpanded && providerProfile?.provider_id && !fetchedProfile) {
      setProfileLoading(true);
      setIsBaseApiLoading(true);
      try {
        // Fetch from /providers/:id/base endpoint
        const baseData = await fetchProviderBase(providerProfile.provider_id);
        setFetchedProfile(baseData);
        setIsBaseApiLoading(false);

        // Fetch media for the profile images
        setIsProfileImageLoading(true);
        try {
          const mediaData = await fetchProviderMedia(providerProfile.provider_id);
          // Handle response format: { media: [...] } or direct array
          const mediaArray = mediaData?.media || (Array.isArray(mediaData) ? mediaData : []);
          
          if (mediaArray && Array.isArray(mediaArray) && mediaArray.length > 0) {
            // Sort by isPrimary (primary first) and then by displayOrder
            const sortedMedia = [...mediaArray].sort((a: any, b: any) => {
              if (a.isPrimary && !b.isPrimary) return -1;
              if (!a.isPrimary && b.isPrimary) return 1;
              return (a.displayOrder || 0) - (b.displayOrder || 0);
            });
            
            const imageUrls = sortedMedia
              .map((item: any) => item.mediaUrl)
              .filter((url: string | null | undefined) => url && url !== "null" && url !== "");
            
            if (imageUrls.length > 0) {
              setProfileImages(imageUrls);
            } else if (baseData.heroMediaUrl) {
              setProfileImages([baseData.heroMediaUrl]);
            }
          } else if (baseData.heroMediaUrl) {
            setProfileImages([baseData.heroMediaUrl]);
          }
        } catch (mediaError) {
          // Fallback to heroMediaUrl from base if media API fails
          if (baseData.heroMediaUrl) {
            setProfileImages([baseData.heroMediaUrl]);
          }
        } finally {
          setIsProfileImageLoading(false);
        }
      } catch (error) {
        // Handle error silently
        console.error('Error fetching profile:', error);
        setIsBaseApiLoading(false);
      } finally {
        setProfileLoading(false);
      }
    }
  };

  return {
    profileLoading,
    fetchedProfile,
    isProfileImageLoading,
    isBaseApiLoading,
    handleViewProfileClick,
  };
};

